
import Vue from 'vue'
import ProjectMixin from '@/_mixins/project'

import { getRequest } from '@/plugins/api-axios'
import { deserialise, serialise } from 'kitsu-core'

import FormResponseViewerDialog from '@/views/Project/PSI/dialogs/formResponseViewer.vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import FormResponse from '@/views/Project/Form/components/formResponse.vue'
import CourseItem from '@/views/Project/PSI/components/courseItem.vue'
import { Course } from '@/schema'
import { SELF_ATTESTABLE_PSI_COURSES_QUERY } from '../gql/queries'
import {
    CREATE_SELF_ATTESTED_COMPLETION_MUTATION,
    DELETE_COURSE_COMPLETION_MUTATION,
} from '../gql/mutations'

class CourseViewModel {
    isUpdating = false
    course: Course

    constructor(course: Course) {
        this.course = JSON.parse(JSON.stringify(course))
    }
}

const FormResponseDialog = Vue.extend({
    mixins: [ProjectMixin],
    components: { CourseItem, DialogTemplate, FormResponse },

    props: {
        props: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            language: 'eng',
            formResponse: null as null | Record<string, any>,
            latestResponse: null as null | Record<string, any>,
            saveStatus: null as 'saving' | 'saved' | null,
            completedCourses: [] as Array<string>,
            psiCompletion: this.props.psiCompletion,
            requiredCourses: [] as CourseViewModel[],
            optionalCourses: [] as CourseViewModel[],
        }
    },

    computed: {
        revision() {
            return this.props.revision
        },
        psi() {
            return this.props.psi
        },
        languages() {
            return this.props.languages
        },
        isPreview() {
            return this.props.isPreview
        },
        isCoursesAvailable() {
            return this.requiredCourses.length > 0 || this.optionalCourses.length > 0
        },
    },

    methods: {
        async updateCourse(courseItem: CourseViewModel) {
            await this.$nextTick()
            courseItem.isUpdating = true
            try {
                const [courseCompletion] = courseItem.course.myNonExpiredCompletionsForProject
                if (!courseCompletion) {
                    const data = await this.$apollo.mutate({
                        mutation: CREATE_SELF_ATTESTED_COMPLETION_MUTATION,
                        DELETE_COURSE_COMPLETION_MUTATION,
                        variables: {
                            courseId: courseItem.course.id,
                            projectId: this.currentProjectId,
                        },
                    })
                    courseItem.course.myNonExpiredCompletionsForProject.push(
                        data.data.createSelfAttestedCourseCompletion.selfAttestedCourseCompletion
                    )
                } else if (courseCompletion.canDelete) {
                    await this.$apollo.mutate({
                        mutation: DELETE_COURSE_COMPLETION_MUTATION,
                        variables: {
                            completionId: courseCompletion.id,
                        },
                    })
                    courseItem.course.myNonExpiredCompletionsForProject.splice(0, 1)
                }
            } finally {
                courseItem.isUpdating = false
            }
        },
        async createResponse() {
            let payload = serialise('psiCompletion', {
                type: 'psiCompletion',
                psi: { data: { type: this.psi.type, id: this.psi.id } },
            })

            this.psiCompletion = await this.postRequest(
                this.getURL({ path: `psiCompletions`, base: this.psiBaseUrl }),
                payload.data
            ).catch(() => {
                this.loading = false
            })

            await this.getFormResponse(this.psiCompletion)
        },

        async getFormResponse(psiCompletion) {
            const ids = {
                open: psiCompletion?.id,
                latest: this.props.latestSubmitedCompletion?.id,
            }

            let searchFormResponses = await getRequest(
                `${this.formBaseUrl}/formResponses?skipLinks`,
                this.headersmixins,
                {
                    'filter[resource]': Object.values(ids).filter(Boolean).join(','),
                    include: 'revision,resource',
                }
            )

            let { data } = deserialise(searchFormResponses.data)
            this.formResponse = data.find(
                (formResponse: any) => formResponse.resource.data.id === ids.open
            )
            if (ids.latest) {
                this.latestResponse = data.find(
                    (formResponse: any) => formResponse.resource.data.id === ids.latest
                )
            }
        },

        submit() {
            this.$emit('close', true)
        },

        setUserLanguageAsSelected() {
            // First trying to find exact match between user language and available languages
            let index = this.props.languages.findIndex((lang) => lang === this.userLanguage)

            // Handeling norwegian language
            if (index == -1) {
                if (this.userLanguage == 'nor' || this.userLanguage == 'nob') {
                    index = this.props.languages.findIndex(
                        (lang) => lang === 'nor' || lang === 'nob'
                    )
                }
            }

            if (index == -1) index = 0

            this.language = this.languages[index]
        },

        viewResponse() {
            this.openDialog(FormResponseViewerDialog, {
                resourceFormResponse: this.props.latestSubmitedCompletion,
            })
        },

        handleSaveStatusChange(status: 'saving' | 'saved' | null) {
            this.saveStatus = status
            if (status) {
                setTimeout(() => {
                    this.saveStatus = null
                }, 3000)
            }
        },
    },

    async created() {
        this.loading = true

        if (this.psi.useCourses) {
            const { data: coursesData } = await this.$apollo.query({
                query: SELF_ATTESTABLE_PSI_COURSES_QUERY,
                variables: {
                    projectId: this.currentProjectId,
                },
                fetchPolicy: 'network-only',
            })

            coursesData?.selfAttestablePsiCourses?.forEach((course: Course) => {
                if (course.isRequiredForCheckIn) {
                    this.requiredCourses.push(new CourseViewModel(course))
                } else {
                    this.optionalCourses.push(new CourseViewModel(course))
                }

                if (course.myNonExpiredCompletionsForProject.length) {
                    this.completedCourses.push(course.id)
                }
            })
        }

        if (this.props.psiCompletion) {
            await this.getFormResponse(this.props.psiCompletion)
        } else {
            await this.createResponse()
        }
        this.loading = false
    },

    async mounted() {
        this.setUserLanguageAsSelected()
    },
})

export default FormResponseDialog
